// Shared fetch wrapper, error classes, and base URL utilities.
//
// Authentication is handled by @logto/express on the backend which uses
// express-session cookies. The browser automatically includes the session
// cookie on same-origin fetch() calls — no interceptor needed.

import { resolveLegalRoute } from "../components/legal/legalContent";
import { appReturnPath } from "../navigation/routes";
import { buildHostedAuthPath } from "./hostedAuth";

// -- Session-expired redirect -----------------------------------------------

let redirecting = false;

/**
 * Whether this page load ever held a session. Set by `fetchCurrentUser()` when
 * `/api/me` comes back 200 — the one call that proves the visitor is signed in.
 */
let sessionEstablished = false;

export function markSessionEstablished(): void {
    sessionEstablished = true;
}

/**
 * Paths App renders without looking at the session: the public legal documents
 * and the password-reset form. The legal route table is the single source of
 * truth, so a document added there is covered here without a second list to
 * keep in step.
 */
function rendersWithoutSession(pathname: string): boolean {
    return pathname === "/reset-password" || resolveLegalRoute(pathname) !== null;
}

/**
 * A 401 means "sign in again" only for someone who was signed in.
 *
 * Bouncing everyone else is what made the whole public site unreachable: the
 * providers in `main.tsx` mount above App's public branches and probe the API
 * on every page, so a logged-out visitor to /terms — the payment gateway's
 * reviewer, say, or anyone reading the welcome page — was sent to the hosted
 * sign-in screen a frame after the page rendered. Two conditions now have to
 * hold: the visitor had a session to lose, and the page they are on belongs to
 * the signed-in app.
 *
 * The throw is unconditional either way, so callers still see the failure and
 * their own fallbacks run.
 */
function handleSessionExpired(): never {
    const redirectWouldBeRight =
        sessionEstablished && !rendersWithoutSession(window.location.pathname);

    if (redirectWouldBeRight && !redirecting) {
        redirecting = true;
        // Signing in again should cost the operator a sign-in, not their
        // place: the page they were reading is where the callback returns.
        window.location.href = buildHostedAuthPath(
            "login",
            appReturnPath(window.location.pathname, window.location.search),
        );
    }
    throw new Error("Session expired");
}

// -- apiFetch — thin wrapper around fetch that redirects on 401 -------------

export async function apiFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> {
    const res = await fetch(input, init);
    if (res.status === 401) {
        handleSessionExpired();
    }
    return res;
}

// -- Error classes ----------------------------------------------------------

export class PmsNotConfiguredError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "PmsNotConfiguredError";
    }
}

export class AccountDeactivatedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AccountDeactivatedError";
    }
}

export class AccountPendingApprovalError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AccountPendingApprovalError";
    }
}

/** A temporary account (minted by an admin) whose access window has closed. */
export class AccountExpiredError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AccountExpiredError";
    }
}

/** The `code` a write carries when {@link ActiveConnectionChangedError} is the reason. */
export const ACTIVE_CONNECTION_CHANGED = "ACTIVE_CONNECTION_CHANGED";

/**
 * A write refused because its form was loaded for another property than the
 * one open now. The open property is shared by every tab, so the page is stale:
 * nothing was written, and a reload shows the property that is open.
 */
export class ActiveConnectionChangedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ActiveConnectionChangedError";
    }
}
